import { Component, AfterViewInit, OnInit, OnDestroy, Input } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/fromEvent';
import 'rxjs/add/operator/throttleTime';
import 'rxjs/add/operator/map';

import { environment } from '../../environments/environment';

import L from 'leaflet';
import 'leaflet.markercluster';
import 'mapbox-gl-leaflet';

import { LeafletStop } from '../core/stop';
import { LeafletRail } from '../core/rail';
import { LeafletTram } from '../core/tram';
import { Route, BuiltRoute, EnhancedBuiltRoute } from '../core/route';
import { PointOfInterest, POIType } from '../core/poi';
import { TramCollector } from './tram-collector';
import { StopCollector } from './stop-collector';
import { RouteCollector } from './route-collector';
import { RailCollector } from './rail-collector';
import { PointOfInterestCollector } from './poi-collector';
import { PanelDataExchangeService } from './panel-data-exchange.service';
import { TramApiService } from '../tram-api.service';

@Component({
    selector: 'map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    animations: [
        trigger('infoPanelState', [
            state('closed', style({
                display: 'none',
                right: '-450px'
            })),
            state('opened', style({})),

            transition('opened => closed', animate('0.1s ease-out')),
            transition('closed => opened', animate('0.2s ease-in'))
        ]),
        trigger('routeInfoPanelState', [
            state('closed', style({
                display: 'none',
                right: '-450px'
            })),
            state('opened', style({
                right: '50px'
            })),
            state('openedItself', style({
                right: '5px'
            })),

            transition('opened => closed', animate('0.1s ease-out')),
            transition('openedItself => closed', animate('0.1s ease-out')),
            transition('closed => opened', animate('0.2s ease-in')),
            transition('closed => openedItself', animate('0.2s ease-in'))
        ]),
        trigger('builtRouteInfoPanelState', [
            state('closed', style({
                display: 'none',
                right: '-450px'
            })),
            state('opened', style({
                right: '50px'
            })),
            state('openedItself', style({
                right: '5px'
            })),

            transition('opened => closed', animate('0.1s ease-out')),
            transition('openedItself => closed', animate('0.1s ease-out')),
            transition('closed => opened', animate('0.2s ease-in')),
            transition('closed => openedItself', animate('0.2s ease-in'))
        ]),
        trigger('legendPanelState', [
            state('closed', style({
                display: 'none',
                right: '-450px'
            })),
            state('opened', style({})),

            transition('opened => closed', animate('0.1s ease-out')),
            transition('closed => opened', animate('0.2s ease-in'))
        ]),
        trigger('poiSearchPanelState', [
            state('closed', style({
                display: 'none',
                right: '-450px'
            })),
            state('opened', style({})),

            transition('opened => closed', animate('0.1s ease-out')),
            transition('closed => opened', animate('0.2s ease-in'))
        ]),
        trigger('poiInfoPanelState', [
            state('closed', style({
                display: 'none',
                right: '-450px'
            })),
            state('opened', style({
                right: '50px'
            })),

            transition('opened => closed', animate('0.1s ease-out')),
            transition('closed => opened', animate('0.2s ease-in'))
        ])
    ]
})
export class MapComponent implements AfterViewInit, OnInit, OnDestroy {
    private readonly railPaneName = 'activeRailPane';
    private readonly stopPaneName = 'activeStopPane';
    private readonly gap = 200;

    private afterInitializeRouteCollectorHandler: (data: any) => void;
    private afterBuildSegmentedRouteHandler: (route: EnhancedBuiltRoute) => void;
    private afterStopsInitializeHandler: any;
    private afterRailsInitializeHandler: any;
    private afterUnselectStopHandler: (stop: LeafletStop) => void;
    private beforeSelectStopHandler: (stop: LeafletStop) => void;
    private afterSelectStopHandler: (stop: LeafletStop) => void;
    private beforeSelectRailHandler: (data: any) => void;
    private afterSelectRailHandler: any;
    private afterInitializeTramHandler: any;
    private beforeSelectTramHandler: (tram: LeafletTram) => void;
    private beforeUnselectRouteHandler: (data: any) => void;
    private beforeUnselectDeeplyRouteHandler: (route?: EnhancedBuiltRoute) => void;
    private afterSelectRouteHandler: (data: any) => void;

    private showRouteInfoPanelHandler: (isShown: boolean) => void;
    private showBuiltRouteInfoPanelHandler: (isShown: boolean) => void;

    private timer = null;
    private touchTimer = null;
    private firstTapTimeStamp = null;

    @Input() initialZoom = 13;
    @Input() isRouteMode = false;
    @Input() isPreview = false;
    @Input() hidePanels = false;
    @Input() hideTrams = false;
    @Input() mapId = 'map';

    map: L.Map;

    railCommonLayer: L.FeatureGroup;
    stopCommonLayer: L.FeatureGroup;
    activeRailCommonLayer: L.FeatureGroup;
    activeStopCommonLayer: L.FeatureGroup;
    activePOICommonLayer: L.FeatureGroup;
    markerCommonLayer: L.FeatureGroup;
    tramMarkerLayer: L.FeatureGroup;
    tramPopupLayer: L.FeatureGroup;

    stateRouteInfoPanel = 'closed';
    stateBuiltRouteInfoPanel = 'closed';
    stateInfoPanel = 'closed';
    stateLegendPanel = 'closed';
    statePOISearchPanel = 'closed';
    statePOIInfoPanel = 'closed';

    activatedRouteParams: ParamMap;

    selectedMapObject: any;
    selectedPOI: PointOfInterest;

    isTryToScrollMap: boolean;
    isTryToTouchMap: boolean;
    isMapControlModeEnabled = false;

    get isMobile() {
        return (typeof window.orientation !== 'undefined') || (navigator.userAgent.indexOf('IEMobile') !== -1);
    }

    constructor(
        private activatedRoute: ActivatedRoute,
        private poiCollector: PointOfInterestCollector,
        private routeCollector: RouteCollector,
        private stopCollector: StopCollector,
        private railCollector: RailCollector,
        private tramCollector: TramCollector,
        private api: TramApiService,
        private exchanger: PanelDataExchangeService) {

        activatedRoute.paramMap.subscribe(p => {
            if (p.keys.length <= 0) {
                return;
            }

            this.activatedRouteParams = p;
        });
    }

    onScrolledOver(event: WheelEvent) {
        if (event.ctrlKey) {
            event.stopPropagation();
            event.preventDefault();

            if (this.timer != null) {
                this.isTryToScrollMap = false;
                clearTimeout(this.timer);
                this.timer = null;
            }

            if (event.deltaY < 0) {
                this.zoomIn();
            } else if (event.deltaY > 0) {
                this.zoomOut();
            }
            return;
        }

        if (!this.isTryToScrollMap) {
            this.isTryToScrollMap = true;

            this.timer = setTimeout(_ => {
                this.isTryToScrollMap = false;
            }, 1500);
        }
    }

    ngOnInit() {
        if (this.hidePanels) {
            return;
        }

        this.showRouteInfoPanelHandler = isShown => this.onShowRouteInfoPanel(isShown);
        this.exchanger.showRouteInfoPanelHandler.on(this.showRouteInfoPanelHandler);

        this.showBuiltRouteInfoPanelHandler = isShown => {
            this.stateBuiltRouteInfoPanel = isShown ? 'opened' : 'closed';
        };
        this.exchanger.showBuiltRouteInfoPanelHandler.on(this.showBuiltRouteInfoPanelHandler);

        this.exchanger.showInfoPanelHandler.on(isShown => {
            this.stateInfoPanel = isShown ? 'opened' : 'closed';
        });
        this.exchanger.showLegendPanelHandler.on(isShown => {
            this.stateLegendPanel = isShown ? 'opened' : 'closed';
            this.statePOISearchPanel = isShown ? 'opened' : 'closed';
        });
        this.exchanger.showPOIInfoPanelHandler.on(isShown => {
            this.statePOIInfoPanel = isShown ? 'opened' : 'closed';
        });
    }

    ngAfterViewInit() {
        this.initMap();

        this.initRoutes();

        if (this.isRouteMode) {
            this.initPOI();
        }

        if (!this.isRouteMode && !this.hideTrams) {
            this.initTrams();
        }
    }

    ngOnDestroy() {
        this.tramCollector.finish();
        this.activatedRouteParams = undefined;

        this.routeCollector.afterBuildSegmentedRoute.off(this.afterBuildSegmentedRouteHandler);
        this.routeCollector.afterInitialize.off(this.afterInitializeRouteCollectorHandler);
        this.routeCollector.beforeUnselect.off(this.beforeUnselectRouteHandler);
        this.routeCollector.beforeUnselectDeeply.off(this.beforeUnselectDeeplyRouteHandler);
        this.routeCollector.afterSelect.off(this.afterSelectRouteHandler);
        this.stopCollector.afterUnselect.off(this.afterUnselectStopHandler);
        this.stopCollector.beforeSelect.off(this.beforeSelectStopHandler);
        this.stopCollector.afterSelect.off(this.afterSelectStopHandler);
        this.stopCollector.afterInitialize.off(this.afterStopsInitializeHandler);
        this.railCollector.afterInitialize.off(this.afterRailsInitializeHandler);
        this.railCollector.beforeSelect.off(this.beforeSelectRailHandler);
        this.railCollector.afterSelect.off(this.afterSelectRailHandler);
        this.tramCollector.afterInitializeTram.off(this.afterInitializeTramHandler);
        this.tramCollector.beforeSelect.off(this.beforeSelectTramHandler);

        this.afterBuildSegmentedRouteHandler = null;
        this.afterInitializeRouteCollectorHandler = null;
        this.afterUnselectStopHandler = null;
        this.beforeSelectStopHandler = null;
        this.afterSelectStopHandler = null;
        this.afterStopsInitializeHandler = null;
        this.afterRailsInitializeHandler = null;
        this.beforeSelectRailHandler = null;
        this.afterSelectRailHandler = null;
        this.afterInitializeTramHandler = null;
        this.beforeSelectTramHandler = null;
        this.beforeUnselectRouteHandler = null;
        this.beforeUnselectDeeplyRouteHandler = null;
        this.afterSelectRouteHandler = null;

        this.exchanger.showRouteInfoPanelHandler.off(this.showRouteInfoPanelHandler);
        this.exchanger.showRouteInfoPanelHandler.off(this.showBuiltRouteInfoPanelHandler);

        this.showRouteInfoPanelHandler = null;
        this.showBuiltRouteInfoPanelHandler = null;

        this.routeCollector.unselect();
        this.selectedMapObject = null;

        this.railCommonLayer = null;
        this.stopCommonLayer = null;
        this.activeRailCommonLayer = null;
        this.activeStopCommonLayer = null;
        this.activePOICommonLayer = null;
        this.markerCommonLayer = null;
        this.tramMarkerLayer = null;
        this.tramPopupLayer = null;
    }

    zoomIn(): void {
        this.map.zoomIn(1);
    }
    zoomOut(): void {
        this.map.zoomOut(1);
    }

    wrappedSearchPOI = (substring) => {
        return this.searchPOI(substring);
    }

    onSelectedPOIChanged(e: PointOfInterest) {
        const newCenter = L.latLng(e.position.latitude, e.position.longitude);
        this.map.setView(newCenter, 17);

        this.poiCollector.select(e);
    }

    disableMapControlMode() {
        this.isMapControlModeEnabled = false;
    }
    handleMobileMode() {
        Observable.fromEvent(document.getElementById('mapProtector'), 'touchstart')
            .subscribe((event: PointerEvent) => {
                if (this.firstTapTimeStamp === null) {
                    this.touchTimer = setTimeout(() => {
                        this.isMapControlModeEnabled = false;

                        this.timer = setTimeout(_ => {
                            this.isMapControlModeEnabled = false;
                            this.firstTapTimeStamp = null;
                        }, 1500);
                    }, this.gap);

                    // init first tap
                    this.firstTapTimeStamp = event.timeStamp;
                    return;
                }

                // already have first tap time stamp, then check second tap to define if double tap is
                const differenceTimeStamp = event.timeStamp - this.firstTapTimeStamp;
                if (differenceTimeStamp >= 0 && differenceTimeStamp <= this.gap) {
                    if (this.touchTimer != null) {
                        clearTimeout(this.touchTimer);
                        this.touchTimer = null;
                    }

                    // just in case
                    if (this.timer != null) {
                        clearTimeout(this.timer);
                        this.timer = null;
                    }

                    // double tap is detected, enable MAP CONTROL mode
                    this.isMapControlModeEnabled = true;
                }

                this.firstTapTimeStamp = null;

                event.stopPropagation();
                event.preventDefault();
            });
    }

    private initMap() {
        const spbCenter = L.latLng(59.96025263846721, 30.478477478027347);
        const spbBound = [[60.345, 31.173], [59.556, 29.168]];
        const options = {
            zoomControl: false,
            center: spbCenter,
            zoom: this.initialZoom,
            maxZoom: 17,
            minZoom: 11,
            maxBounds: spbBound,
            scrollWheelZoom: false
        };

        if (this.isMobile) {
            this.handleMobileMode();
        }

        this.map = L.map(this.mapId, options);
        const attribution = '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>';
        // MAP OF RASTRs
        let tileLayer = L.tileLayer('http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: attribution });
        tileLayer.addTo(this.map);
        //2gis
        //let tileLayer = L.tileLayer('http://tile2.maps.2gis.com/tiles?x={x}&y={y}&z={z}', { attribution: attribution });
        //tileLayer.addTo(this.map);

        // MAP OF VECTOR DATA (use own server of tiles)
        const { url: mapTileUrl, stylePath: mapStylePath } = environment.vectorMap;

        const vectorLayer = L.mapboxGL({
            attribution: attribution,
            accessToken: 'not-needed',
            style: mapTileUrl + mapStylePath,
            transformRequest: function(url: string, resourceType: string) {
                if (resourceType === "Tile") {
                    const urlObject = new URL(url);
                    url = url.replace(urlObject.origin, mapTileUrl)
                }
                return { url };
            }
        });
        vectorLayer.addTo(this.map);


        let activeRailPane = this.map.getPane(this.railPaneName);
        if (!activeRailPane) {
            activeRailPane = this.map.createPane(this.railPaneName);
            activeRailPane.style.zIndex = '440';
        }
        let activeStopPane = this.map.getPane(this.stopPaneName);
        if (!activeStopPane) {
            activeStopPane = this.map.createPane(this.stopPaneName);
            activeStopPane.style.zIndex = '450';
        }

        this.railCommonLayer = L.featureGroup().addTo(this.map);
        this.stopCommonLayer = L.featureGroup().addTo(this.map);
        this.activeRailCommonLayer = L.featureGroup({ pane: this.railPaneName }).addTo(this.map);
        this.activeStopCommonLayer = L.featureGroup({ pane: this.stopPaneName }).addTo(this.map);

        this.markerCommonLayer = L.featureGroup().addTo(this.map);
        this.tramMarkerLayer = L.featureGroup().addTo(this.map);
        this.tramPopupLayer = L.featureGroup().addTo(this.map);

        if (this.isRouteMode) {
            this.activePOICommonLayer = L.markerClusterGroup({
                showCoverageOnHover: false,
                spiderfyOnMaxZoom: false,

                disableClusteringAtZoom: 15,
                maxClusterRadius: 40
            }).addTo(this.map);
        }
    }

    private initRoutes() {
        this.routeCollector.onBeforeInitialize = () => {
            this.subscribeStopCallbacks();
            this.subscribeRailCallbacks();
        };

        this.afterInitializeRouteCollectorHandler = _ => this.onAfterInitializeRouteCollector();
        this.routeCollector.afterInitialize.on(this.afterInitializeRouteCollectorHandler);

        this.afterBuildSegmentedRouteHandler = (route: EnhancedBuiltRoute) => this.onAfterBuildSegmentedRoute(route);
        this.routeCollector.afterBuildSegmentedRoute.on(this.afterBuildSegmentedRouteHandler);

        this.beforeUnselectDeeplyRouteHandler = (route?: EnhancedBuiltRoute) => this.onBeforeUnselectDeeplyRoute(route);
        this.routeCollector.beforeUnselectDeeply.on(this.beforeUnselectDeeplyRouteHandler);

        this.beforeUnselectRouteHandler = (data: any) => this.onBeforeUnselectRoute(data);
        this.routeCollector.beforeUnselect.on(this.beforeUnselectRouteHandler);

        this.afterSelectRouteHandler = (data: any) => this.onAfterSelectRoute(data);
        this.routeCollector.afterSelect.on(this.afterSelectRouteHandler);

        this.routeCollector.init();
    }

    private initTrams() {
        this.tramCollector.start();

        this.afterInitializeTramHandler = (tram: LeafletTram) => this.onAfterInitializeTram(tram);
        this.tramCollector.afterInitializeTram.on(this.afterInitializeTramHandler);

        this.beforeSelectTramHandler = (tram: LeafletTram) => this.onBeforeSelectTram(tram);
        this.tramCollector.beforeSelect.on(this.beforeSelectTramHandler);
    }

    private initPOI() {
        this.poiCollector.onPOIInitialized = (point: PointOfInterest) => {
            this.activePOICommonLayer.addLayer(point.marker);
            this.exchanger.showLegendPanel(true);
        };

        this.poiCollector.beforeSelect.on((point: PointOfInterest) => {
            this.routeCollector.unselect();
            this.exchanger.showInfoPanel(false);
            this.exchanger.showRouteInfoPanel(false);

            if (this.selectedPOI && point.id !== this.selectedPOI.id) {
                this.selectedPOI = null;
            }
        });
        this.poiCollector.afterSelect.on((point: PointOfInterest) => {
            this.exchanger.showPOIInfoPanel(true);
        });
        this.poiCollector.afterEnable.on(data => {
            for (const point of data.enabledPoints) {
                this.activePOICommonLayer.addLayer(point.marker);
            }

            this.activePOICommonLayer.refreshClusters();
        });
        this.poiCollector.afterDisable.on(data => {
            const selectedPoint = this.poiCollector.points.some(p => p.isSelected);
            if (!selectedPoint) {
                this.exchanger.showPOIInfoPanel(false);
            }

            for (const point of data.disabledPoints) {
                this.activePOICommonLayer.removeLayer(point.marker);
            }

            this.activePOICommonLayer.refreshClusters();
        });

        this.poiCollector.init();
    }

    private subscribeStopCallbacks() {
        this.stopCollector.onStopInitialized = (stop: LeafletStop) => {
            if (this.stopCommonLayer) {
                this.stopCommonLayer.addLayer(stop.circle);
            }
        };

        this.afterStopsInitializeHandler = (stops: LeafletStop[]) => this.onAfterStopsInitialize(stops);
        this.stopCollector.afterInitialize.on(this.afterStopsInitializeHandler);

        this.afterUnselectStopHandler = (stop: LeafletStop) => this.onAfterUnselectStop(stop);
        this.stopCollector.afterUnselect.on(this.afterUnselectStopHandler);

        this.beforeSelectStopHandler = (stop: LeafletStop) => this.onBeforeSelectStop(stop);
        this.stopCollector.beforeSelect.on(this.beforeSelectStopHandler);

        this.afterSelectStopHandler = (stop: LeafletStop) => this.onAfterSelectStop(stop);
        this.stopCollector.afterSelect.on(this.afterSelectStopHandler);
    }
    private subscribeRailCallbacks() {
        this.railCollector.onRailInitialized = (rail: LeafletRail) => {
            if (this.railCommonLayer) {
                this.railCommonLayer.addLayer(rail.polyline);
            }
        };

        this.afterRailsInitializeHandler = (rails: LeafletRail[]) => this.onAfterRailsInitialize(rails);
        this.railCollector.afterInitialize.on(this.afterRailsInitializeHandler);

        this.beforeSelectRailHandler = (rail: LeafletRail) => this.onBeforeSelectRail(rail);
        this.railCollector.beforeSelect.on(this.beforeSelectRailHandler);

        this.afterSelectRailHandler = (data: any) => this.onAfterSelectRail(data.event, data.rail);
        this.railCollector.afterSelect.on(this.afterSelectRailHandler);
    }

    private onAfterInitializeRouteCollector() {
        setTimeout(function () {
            const preloader = document.getElementById('preloader');
            if (preloader) {
                preloader.classList.add('closed');
            }
            document.body.classList.remove('on-preloading');

            setTimeout(function () {
                if (preloader && preloader.parentNode) {
                    preloader.parentNode.removeChild(preloader);
                }
            }, 650);
        }, 500);
    }
    private onAfterBuildSegmentedRoute(route: EnhancedBuiltRoute) {
        this.activeRailCommonLayer.clearLayers();

        for (const segment of route.segments) {
            segment.rails.forEach(r => {
                this.activeRailCommonLayer.addLayer(r.polyline);
            });

            segment.stops.forEach(s => {
                this.bringToFront(s as LeafletStop);
            });
        }
    }
    private onBeforeUnselectRoute(data: any) {
        if (!data.selectedRoute || data.isReverse === null) {
            return;
        }

        const stopIds = data.isReverse
            ? data.selectedRoute.reverseStopIds
            : data.selectedRoute.stopIds;
        stopIds.forEach(id => {
            const stop = this.stopCollector.get(id) as LeafletStop;
            this.resetSelection(stop);
        });
    }
    private onBeforeUnselectDeeplyRoute(route: EnhancedBuiltRoute) {
        if (!route) {
            return;
        }

        this.activeRailCommonLayer.clearLayers();
    }
    private onAfterSelectRoute(data: any) {
        const route = data.route as Route;
        const isReverse = data.conste;

        const stopIds = isReverse ? route.reverseStopIds : route.stopIds;
        stopIds.forEach(id => {
            const stop = this.stopCollector.get(id) as LeafletStop;
            this.bringToFront(stop);
        });
    }

    private onAfterUnselectStop(stop: LeafletStop) {
        this.resetSelection(stop);
    }
    private onBeforeSelectStop(stop: LeafletStop) {
        this.markerCommonLayer.clearLayers();
        this.activeRailCommonLayer.clearLayers();
        this.routeCollector.unselect();
        this.poiCollector.unselect();

        this.selectedMapObject = null;

        this.exchanger.showRouteInfoPanel(false);
    }
    private onAfterSelectStop(stop: LeafletStop) {
        const marker = L.marker([stop.coordinate.latitude, stop.coordinate.longitude]);

        this.selectedMapObject = stop;
        this.bringToFront(stop);

        if (this.isRouteMode) {
            const selectedPoint = this.poiCollector.points.some(p => p.isSelected);
            if (!selectedPoint) {
                this.exchanger.showPOIInfoPanel(false);
            }
        }
        this.exchanger.showInfoPanel(true);
    }
    private onAfterStopsInitialize(stops: LeafletStop[]) {
        // this.backgroundCommonLayer.bringToBack();
        this.stopCommonLayer.bringToFront();

        this.predefineSegmentedRoute();
        this.changeCenter();
    }

    private onAfterRailsInitialize(rails: LeafletRail[]) {
        // this.backgroundCommonLayer.bringToBack();
        this.stopCommonLayer.bringToFront();
    }
    private onBeforeSelectRail(rail: LeafletRail) {
        this.markerCommonLayer.clearLayers();
        this.activeRailCommonLayer.clearLayers();
        this.routeCollector.unselect();
        this.poiCollector.unselect();

        this.selectedMapObject = null;

        this.exchanger.showRouteInfoPanel(false);
    }
    private onAfterSelectRail(e: any, rail: LeafletRail) {
        if (e && e.latlng) {
            const marker = L.marker(e.latlng);
            // this.markerCommonLayer.addLayer(marker);
        }

        this.selectedMapObject = rail;
        this.exchanger.showInfoPanel(true);
    }

    private onAfterInitializeTram(tram: LeafletTram) {
        if (tram.position.coordinate && tram.position.coordinate.latitude && tram.position.coordinate.longitude) {
            this.tramMarkerLayer.addLayer(tram.marker);
        }
    }
    private onBeforeSelectTram(tram: LeafletTram) {
        this.selectedMapObject = null;

        this.activeRailCommonLayer.clearLayers();
        this.markerCommonLayer.clearLayers();
        this.routeCollector.select(tram);

        this.exchanger.showRouteInfoPanel(false);
        this.exchanger.showInfoPanel(true);
    }

    private onShowRouteInfoPanel(isShown: boolean) {
        if (isShown) {
            this.stateRouteInfoPanel = this.stateInfoPanel === 'opened' ? 'opened' : 'openedItself';
        } else {
            if (this.stateRouteInfoPanel === 'closed') {
                return;
            }
            this.stateRouteInfoPanel = 'closed';

            this.selectPreviousMapObject();
        }
    }

    private predefineSegmentedRoute() {
        if (!this.activatedRouteParams) {
            return;
        }

        let startStopId: number;
        let finishStopId: number;

        if (this.activatedRouteParams.has('startStopId')) {
            startStopId = +this.activatedRouteParams.get('startStopId');
        }

        if (this.activatedRouteParams.has('finishStopId')) {
            finishStopId = +this.activatedRouteParams.get('finishStopId');
        }

        if (startStopId && finishStopId) {
            this.exchanger.showInfoPanel(true);
        }
    }

    private changeCenter() {
        let top: number;
        let left: number;
        let right: number;
        let bottom: number;

        this.routeCollector.routes.forEach((r, index) => {
            let stop = this.stopCollector.get(r.stopIds[0]);

            if (index === 0) {
                top = stop.coordinate.latitude;
                bottom = stop.coordinate.latitude;
                left = stop.coordinate.longitude;
                right = stop.coordinate.longitude;
            } else {
                if (stop.coordinate.latitude > top) {
                    top = stop.coordinate.latitude;
                } else if (stop.coordinate.latitude < bottom) {
                    bottom = stop.coordinate.latitude;
                }

                if (stop.coordinate.longitude > right) {
                    right = stop.coordinate.longitude;
                } else if (stop.coordinate.longitude < left) {
                    left = stop.coordinate.longitude;
                }
            }

            stop = this.stopCollector.get(r.stopIds[r.stopIds.length - 1]);

            if (stop.coordinate.latitude > top) {
                top = stop.coordinate.latitude;
            } else if (stop.coordinate.latitude < bottom) {
                bottom = stop.coordinate.latitude;
            }

            if (stop.coordinate.longitude > right) {
                right = stop.coordinate.longitude;
            } else if (stop.coordinate.longitude < left) {
                left = stop.coordinate.longitude;
            }
        });

        const centerLatitude = bottom + (top - bottom) / 2;
        const centerLongitude = right + (left - right) / 2;

        const newCenter = L.latLng(centerLatitude, centerLongitude);
        this.map.setView(newCenter);
    }

    private selectPreviousMapObject() {
        if (this.selectedMapObject) {
            if (this.selectedMapObject instanceof LeafletRail) {
                this.railCollector.selectGently(this.selectedMapObject.id);
            } else if (this.selectedMapObject instanceof LeafletStop) {
                this.stopCollector.selectGently(this.selectedMapObject.id);
                this.bringToFront(this.selectedMapObject);
            }
        }
    }

    private bringToFront(stop: LeafletStop): void {
        this.stopCommonLayer.removeLayer(stop.circle);
        this.activeStopCommonLayer.addLayer(stop.circle);
    }
    private resetSelection(stop: LeafletStop) {
        this.activeStopCommonLayer.removeLayer(stop.circle);
        this.stopCommonLayer.addLayer(stop.circle);
    }

    private searchPOI(substring: string): Observable<PointOfInterest[]> {
        if (!substring) {
            const observedEmpty = new Observable<PointOfInterest[]>(observer => {
                observer.next([]);
                observer.complete();
            });
            return observedEmpty;
        }
        const lowerSubstring = substring.toLowerCase();
        let suggestedPoints = this.poiCollector.points
            .filter(p => p.isEnabled && p.name.toLowerCase().indexOf(lowerSubstring) >= 0);

        const mappedPoints = new Map<string, PointOfInterest>();
        suggestedPoints = suggestedPoints.filter(s => {
            if (mappedPoints.has(s.name)) {
                return false;
            }

            mappedPoints.set(s.name, s);
            return true;
        });

        if (suggestedPoints.length > 8) {
            suggestedPoints = suggestedPoints.slice(0, 8);
        }

        const observedPoints = new Observable<PointOfInterest[]>(observer => {
            observer.next(suggestedPoints);
            observer.complete();
        });

        return observedPoints;
    }
}



// WEBPACK FOOTER //
// D:/programming/Чижик_Api_Site_Sources-Electroline/front/src/app/common/map.component.ts